import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";

function LockerData() {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);

    const [sites, setSites] = useState([]);
    const [newUrl, setNewUrl] = useState("");
    const [newSiteName, setNewSiteName] = useState("");

    const [url, setUrl] = useState(() => sessionStorage.getItem('scheduleUrl') || "");
    const [recipients, setRecipients] = useState(() => sessionStorage.getItem('scheduleRecipients') || "");
    const [scheduleTime, setScheduleTime] = useState(() => sessionStorage.getItem('scheduleTime') || "09:00");
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    
    // Pagination state for the sites table
    const [currentPage, setCurrentPage] = useState(1);
    const [sitesPerPage] = useState(5);

    // Save form data to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('scheduleUrl', url);
    }, [url]);
    useEffect(() => {
        sessionStorage.setItem('scheduleRecipients', recipients);
    }, [recipients]);
    useEffect(() => {
        sessionStorage.setItem('scheduleTime', scheduleTime);
    }, [scheduleTime]);

    // Fetch sites from the database on initial load
    useEffect(() => {
        fetchSites();
    }, []);

    const fetchSites = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sites`);
            const data = await response.json();
            setSites(data);
        } catch (err) {
            setError("Failed to fetch sites from the database.");
        }
    };

    const handleFileUpload = (event) => {
        setError(null);
        setResults([]);
        setProgress(0);
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                let sitesData = jsonData
                    .slice(1)
                    .map((row) => ({
                        url: row[0],
                        siteName: row[1],
                    }))
                    .filter(
                        (item) =>
                            typeof item.url === "string" &&
                            item.url.trim().length > 0 &&
                            typeof item.siteName === "string" &&
                            item.siteName.trim().length > 0
                    );

                if (sitesData.length === 0) {
                    setError(
                        "No URLs and Site Names found in the first two columns of the Excel file."
                    );
                    return;
                }

                analyzeUrls(sitesData);
            } catch (err) {
                setError("Failed to parse Excel file.");
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const analyzeUrls = async (sitesData) => {
        setLoading(true);
        setError(null);
        setResults([]);
        setProgress(5);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/analyze`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ sitesData }),
                }
            );

            if (!response.ok) {
                throw new Error(`Server error: ${response.statusText}`);
            }

            const data = await response.json();
            setResults(data);
        } catch (err) {
            setError(`Error during analysis: ${err.message}`);
            setResults(
                sitesData.map((siteInfo) => ({
                    ...siteInfo,
                    error: err.message,
                    clicks: "-",
                    impressions: "-",
                    ctr: "-",
                    position: "-",
                }))
            );
        } finally {
            setLoading(false);
            setProgress(100);
        }
    };

    const handleScheduleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setMessage("Scheduling report...");
        setMessageType('info');

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/schedule`,
                { 
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ url, recipients, scheduleTime }) 
                }
            );

            if (!response.ok) {
                throw new Error("Failed to schedule the report.");
            }

            console.log("Report scheduled successfully:", await response.json());
            setMessage("Report scheduled successfully! An initial report has been sent.");
            setMessageType('success');

            setUrl("");
            setRecipients("");
            setScheduleTime("09:00");
            sessionStorage.removeItem('scheduleUrl');
            sessionStorage.removeItem('scheduleRecipients');
            sessionStorage.removeItem('scheduleTime');

        } catch (err) {
            setError(err.message);
            setMessage(`Error: ${err.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };
    
    // NEW: CRUD functions
    const handleAddSite = async (e) => {
        e.preventDefault();
        if (!newUrl || !newSiteName) {
            setError("Both URL and Site Name are required.");
            return;
        }
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sites`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    url: newUrl,
                    siteName: newSiteName,
                }),
            });
            const data = await response.json();
            setSites([...sites, data]);
            setNewUrl("");
            setNewSiteName("");
            setError(null);
        } catch (err) {
            setError(`Failed to add site: ${err.message}`);
        }
    };

    const handleDeleteSite = async (id) => {
        try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sites/${id}`, {
                method: "DELETE"
            });
            setSites(sites.filter(site => site._id !== id));
            setError(null);
        } catch (err) {
            setError(`Failed to delete site: ${err.message}`);
        }
    };

    const handleCheckSite = async (site) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sites/check`, { 
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    sitesData: [{ url: site.url, siteName: site.siteName }],
                    recipients: recipients.split(',').map(email => email.trim())
                })
            });
            const data = await response.json();
            setResults(data);
            // Replace alert with a more user-friendly modal or message
            setMessage("Analysis complete. Check your email for the report!");
            setMessageType('success');
        } catch (err) {
            setError(`Failed to check site: ${err.message}`);
            setMessage(`Error: ${err.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };
    
    // NEW: Check All Sites
    const handleCheckAllSites = async () => {
        setLoading(true);
        setError(null);
        setMessage("");
        setMessageType('info');
        try {
            // Fetch all sites from DB
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sites`);
            const allSites = await response.json();
            if (!Array.isArray(allSites) || allSites.length === 0) {
                setError("No sites found in the database.");
                setLoading(false);
                return;
            }
            // Prepare data for analyze API
            const sitesData = allSites.map(site => ({ url: site.url, siteName: site.siteName }));
            // Call analyze API
            const analyzeResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analyze`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sitesData })
            });
            if (!analyzeResponse.ok) {
                throw new Error(`Server error: ${analyzeResponse.statusText}`);
            }
            const data = await analyzeResponse.json();
            setResults(data);
            setMessage("Analysis complete. Check your email for the report!");
            setMessageType('success');
        } catch (err) {
            setError(`Failed to check all sites: ${err.message}`);
            setMessage(`Error: ${err.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };
    
    // Pagination Logic
    const indexOfLastSite = currentPage * sitesPerPage;
    const indexOfFirstSite = indexOfLastSite - sitesPerPage;
    const currentSites = sites.slice(indexOfFirstSite, indexOfLastSite);

    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(sites.length / sitesPerPage); i++) {
        pageNumbers.push(i);
    }

    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    return (
        <>
            <div className="mx-auto p-4 font-sans text-white">
                <div className="flex gap-8">
                    <div className="flex-1"> 
                        <h2 className="text-2xl font-semibold mb-4">
                            Google Search Console Analyzer (Upload Excel)
                        </h2>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFileUpload}
                            disabled={loading}
                            className="mb-4 block"
                        />
                        
                        {loading && (
                            <p className="text-blue-600 text-white">
                                Analyzing URLs... ({progress.toFixed(0)}%)
                            </p>
                        )}
                        {error && (
                            <p className="text-red-600 font-medium whitespace-pre-wrap">
                                <b>Error:</b> {error}
                            </p>
                        )}

                        {results.length > 0 && (
                            <div className="overflow-x-auto mt-6 mx-auto w-fit">
                                <table className="max-w-xl border border-gray-300">
                                    <thead>
                                        <tr className="bg-gray-200 text-black">
                                            <th className="border px-4 py-2 w-48 max-w-48 break-words">URL</th>
                                            <th className="border px-4 py-2">Site Name</th>
                                           {/*  <th className="border px-4 py-2">Clicks</th> */}
                                            <th className="border px-4 py-2">Impressions</th>
                                           {/*  <th className="border px-4 py-2">CTR</th>
                                            <th className="border px-4 py-2">Position</th>
                                            <th className="border px-4 py-2">Error</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map(
                                            ({ url, siteName, clicks, impressions, ctr, position, error }, i) => (
                                                <tr key={i} className="bg-white hover:bg-gray-100 text-black">
                                                    <td className="border px-4 py-2 w-48 max-w-48 break-words">{url}</td>
                                                    <td className="border px-4 py-2">{siteName ?? "-"}</td>
                                                    {/* <td className="border px-4 py-2">{clicks ?? "-"}</td> */}
                                                    <td className="border px-4 py-2">{impressions ?? "-"}</td>
                                                    {/* <td className="border px-4 py-2">{ctr ?? "-"}</td>
                                                    <td className="border px-4 py-2">{position ?? "-"}</td>
                                                    <td className="border px-4 py-2 text-red-500">{error || "-"}</td> */}
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="flex-1"> 
                        <h2 className="text-2xl font-semibold mb-4">
                            Manage Sites (CRUD)
                            <span>
                                <a
                                    href="/LookerStudioAllOnehub.xlsx"
                                    download="LookerStudioAllOnehub.xlsx"
                                    style={{ color: 'green', textDecoration: 'underline' }}
                                    className="ml-2 text-green-500 hover:text-green-600"
                                >
                                    Download Sample Excel Sheet
                                </a>
                            </span>
                        </h2>
                        <form onSubmit={handleAddSite} className="mb-4">
                            <div className="flex space-x-2 mb-2">
                                <input
                                    type="url"
                                    value={newUrl}
                                    onChange={(e) => setNewUrl(e.target.value)}
                                    className="w-1/2 p-2 border border-gray-300 rounded-md text-black"
                                    placeholder="Enter URL"
                                    required
                                />
                                <input
                                    type="text"
                                    value={newSiteName}
                                    onChange={(e) => setNewSiteName(e.target.value)}
                                    className="w-1/2 p-2 border border-gray-300 rounded-md text-black"
                                    placeholder="Enter Site Name"
                                    required
                                />
                            </div>
                            <div className="flex gap-2">
                                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700" disabled={loading}>
                                    Add Site
                                </button>
                                <button type="button" onClick={handleCheckAllSites} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" disabled={loading}>
                                    Check All
                                </button>
                            </div>
                        </form>

                        {sites.length > 0 && (
                            <div className="overflow-x-auto mt-6">
                                <table className="min-w-full border border-gray-300 text-black">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="border px-4 py-2 w-64 max-w-sm break-words">URL</th>
                                            <th className="border px-4 py-2">Site Name</th>
                                            <th className="border px-4 py-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSites.map(site => (
                                            <tr key={site._id} className="bg-white hover:bg-gray-100">
                                                <td className="border px-4 py-2 w-64 max-w-sm break-words">{site.url}</td>
                                                <td className="border px-4 py-2">{site.siteName}</td>
                                                <td className="border px-4 py-2 flex gap-2">
                                                    <button 
                                                        onClick={() => handleCheckSite(site)} 
                                                        className="px-2 py-1 bg-blue-500 text-white rounded-md">
                                                        Check & Mail
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteSite(site._id)} 
                                                        className="px-2 py-1 bg-red-500 text-white rounded-md">
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {/* Pagination Controls */}
                        <nav className="mt-4">
                            <ul className="flex justify-center space-x-2">
                                {pageNumbers.map(number => (
                                    <li key={number}>
                                        <a
                                            onClick={() => paginate(number)}
                                            href="#"
                                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                                                currentPage === number
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 text-black hover:bg-gray-300'
                                            }`}
                                        >
                                            {number}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
                
                <hr className="my-8 border-gray-600" />
               {/*  
                <h2 className="text-2xl font-semibold mb-4">
                    Schedule a Single Report 🚀
                </h2>
                <form onSubmit={handleScheduleSubmit}>
                    <div className="mb-4"></div>
                        <label className="block mb-2">Looker Studio URL</label>
                        <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md text-black"
                            placeholder="e.g., https://lookerstudio.google.com/reporting/..."
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Recipients (comma-separated)</label>
                        <input
                            type="text"
                            value={recipients}
                            onChange={(e) => setRecipients(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md text-black"
                            placeholder="email1@example.com, email2@example.com"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2">Daily Report Time</label>
                        <input
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md text-black"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        disabled={loading}
                    >
                        Schedule Report
                    </button>
                </form> */}
            </div>
        </>
    );
}

export default LockerData;
